import { AppSidebar } from "@/components/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarProvider } from "@/components/ui/sidebar";
import { StorageUtility } from "@/lib/storage";
import { cn, currentEnvironment } from "@/lib/utils";

import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useRouter,
} from "@tanstack/react-router";
import { AlertTriangle, LogOut, Settings } from "lucide-react";
import { z } from "zod/v4";
import { zodValidator } from "@tanstack/zod-adapter";
import { ApplicationSelect } from "@/components/application-select";
import { datadogRum } from "@datadog/browser-rum";
import { getMerchantQuery, MerchantQuery } from "@/lib/merchant-query";
import { SessionExpirationCheck } from "@/routes/auth/_auth-layout/-components/auth/session-expiration-check";
import { OverdueInvoiceBanner } from "@/components/overdue-invoice-banner";
import {
  getJwtFromStorage,
  hasRole,
  parseVolumeJwt,
  redirectToSignIn,
  validateSession,
  validateUserSession,
} from "@/lib/auth";
import { KnownGenericError } from "@/lib/errors";
import { useMerchant } from "@/hooks/use-merchant";
import { useEffect } from "react";
import { usePostHog } from "posthog-js/react";
import { useExchangeRates } from "./_main-layout/_exchange/-components/is-exchange-enabled";

const searchParamsSchema = z.object({
  applicationId: z.string().optional(),
});

const getMerchantId = (tokenContents: ReturnType<typeof parseVolumeJwt>) => {
  const merchantId =
    tokenContents.merchantId ?? StorageUtility.getItem("v_merchantId_v1");
  if (!merchantId) {
    throw redirectToSignIn(false);
  }
  return merchantId;
};

const getSelectedApplication = (
  applications: MerchantQuery["applications"],
  applicationId?: string,
) => {
  const selectedApplication =
    applications.find((a) => a.applicationId === applicationId) ??
    applications.at(0);

  if (!selectedApplication) {
    throw new KnownGenericError({
      userFriendlyMessage: "Invalid application configuration",
    });
  }

  return selectedApplication;
};

const setupDatadogContext = (
  selectedApplication: MerchantQuery["applications"][number],
  loggedInUserEmail: string | null,
  companyName: string,
) => {
  datadogRum.setGlobalContextProperty("environment", currentEnvironment());
  datadogRum.setViewContextProperty(
    "applicationId",
    selectedApplication.applicationId,
  );
  datadogRum.setViewContextProperty("email", loggedInUserEmail);
  datadogRum.setViewContextProperty("merchant", companyName);
};

export const Route = createFileRoute("/(app)/_main-layout")({
  component: () => <LayoutComponent />,
  beforeLoad: async (params) => {
    const token = getJwtFromStorage();
    validateSession(token);

    const tokenContents = parseVolumeJwt(token);
    const loggedInUserEmail = validateUserSession(tokenContents);
    const merchantId = getMerchantId(tokenContents);

    const data = await params.context.queryClient.ensureQueryData({
      ...getMerchantQuery({
        accessToken: token,
        merchantId: merchantId,
      }),
      revalidateIfStale: true,
    });

    const selectedApplication = getSelectedApplication(
      data.applications,
      params.search.applicationId,
    );

    setupDatadogContext(
      selectedApplication,
      loggedInUserEmail,
      data.merchant.companyName,
    );

    return {
      accessToken: token,
      decodedJwt: tokenContents,
      merchantId: data.merchant.merchantId,
      email: loggedInUserEmail,
      authorities: tokenContents.authorities,
      selectedApplicationId: selectedApplication.applicationId,
      userId: tokenContents.sub,
      isAdmin: tokenContents.adminId !== undefined,
      hasVolumeAdminRole:
        hasRole("VOLUME_ADMIN", tokenContents.authorities) ||
        hasRole("VOLUME_ADMIN_READ", tokenContents.authorities),
    };
  },
  validateSearch: zodValidator(searchParamsSchema),
});

const LayoutComponent = () => {
  const merchant = useMerchant();
  const context = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const location = useLocation();
  const router = useRouter();
  const posthog = usePostHog();

  const isAdmin = context.isAdmin;
  useExchangeRates();

  useEffect(() => {
    posthog.setPersonProperties({
      applicationId: context.selectedApplicationId,
      merchantId: context.merchantId,
      merchantName: merchant.companyName,
      isAdmin: context.isAdmin,
    });
  }, []);

  return (
    <SidebarProvider>
      <SessionExpirationCheck decodedJwt={context.decodedJwt} />
      <AppSidebar />
      <main
        className="bg-volume-grey w-full"
        style={{
          scrollbarGutter: "stable",
          width: "max(calc(100% - var(--sidebar-width)), 750px)",
        }}
      >
        <div className="my-4 flex flex-1 flex-col overflow-y-auto pl-8 pr-4">
          <div className="flex flex-col">
            {isAdmin ? (
              <div
                className={cn(
                  "flex items-center mb-4",
                  location.pathname === "/admin" && "w-full",
                )}
              >
                <div className="flex items-center bg-red-300 px-2 rounded-lg h-12 mr-auto">
                  <AlertTriangle className="mr-10" />{" "}
                  <div className="font-bold mr-4">Admin:</div>{" "}
                  {StorageUtility.getItem("v_adminEmail_v1")}
                  <AlertTriangle className="ml-10" />{" "}
                </div>
                {location.pathname === "/admin" ? (
                  <button
                    className="hover:cursor-pointer ml-auto"
                    onClick={() => {
                      StorageUtility.removeAll();
                      router.invalidate();
                    }}
                  >
                    <LogOut className="mr-2 inline" />
                    <div className="">Sign out</div>
                  </button>
                ) : null}
              </div>
            ) : null}
            {!isAdmin || (isAdmin && location.pathname !== "/admin") ? (
              <div className="ml-auto flex gap-4">
                <ApplicationSelect applications={merchant.applications ?? []} />
                <div className="flex-none">
                  <DropdownMenu>
                    <DropdownMenuTrigger className="h-12 rounded-lg border-2 border-solid bg-white focus:outline-hidden data-[state=open]:border-volume-text-purple">
                      <div
                        className={cn(
                          "flex items-center rounded-lg bg-white px-3  py-1 ",
                        )}
                      >
                        <div className="mr-3 font-normal">
                          {merchant.companyName}
                        </div>
                        <img
                          src={"/favicon.ico"}
                          width={28}
                          height={28}
                          alt=""
                          className="rounded-md"
                        />
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      onCloseAutoFocus={undefined}
                      align="end"
                    >
                      <Link
                        to="/settings/account-settings"
                        search={(prev) => ({
                          ...(prev?.applicationId
                            ? { applicationId: prev.applicationId }
                            : {}),
                        })}
                        onMouseEnter={() => {
                          router.preloadRoute({
                            to: "/settings/account-settings",
                            search: (prev) => ({
                              ...(prev?.applicationId
                                ? { applicationId: prev.applicationId }
                                : {}),
                            }),
                          });
                        }}
                      >
                        <DropdownMenuItem className="hover:cursor-pointer">
                          <Settings className="mr-2" />
                          Settings
                        </DropdownMenuItem>
                      </Link>
                      <DropdownMenuItem
                        className="hover:cursor-pointer"
                        onClick={() => {
                          StorageUtility.removeAll();
                          navigate({ to: "/auth/signin" });
                        }}
                      >
                        <div>
                          <LogOut className="mr-2 inline" />
                          Sign out
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ) : null}
          </div>
          <OverdueInvoiceBanner />
          <Outlet />
        </div>
      </main>
    </SidebarProvider>
  );
};
